/**
 * KAN-752: состояние и сохранение формы пользователя (оператора).
 *
 * Форма страницы оператора /users/:id (вкладки "Основное" и "Инструкции",
 * модалка EditUserDialog удалена). Поля, проверка изменений,
 * сборка PATCH только из изменённых полей и разбор ошибок 400 живут здесь,
 * разметка полей - в UserMainFields / OperatorInstructionsFields.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  parsePromptSectionsErrors,
  promptSectionsEqual,
  type PromptSectionsErrors,
} from '@/hooks/useOperatorSections';
import { usersApi } from '@/services/api/users';
import { roleTemplatesApi } from '@/services/api/roleTemplates';
import type { PromptSections, UpdateUserPayload, User } from '@/types/user';

interface InitialValues {
  firstName: string;
  lastName: string;
  promptSections: PromptSections;
  template: string | null;
  operatorDisplayName: string;
  useStagePrompts: boolean;
}

export interface UseUserEditFormOptions {
  /** Сохранение прошло. */
  onSaved?: () => void;
}

/** Текст ошибки сохранения: поля DRF через пробел, части инструкций одной фразой. */
function errorMessage(error: unknown): { message: string; sections?: PromptSectionsErrors } {
  const serverData = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
  let sections: PromptSectionsErrors | undefined;
  if (serverData && typeof serverData === 'object') {
    sections = parsePromptSectionsErrors(serverData);
    const message = Object.entries(serverData)
      .map(([field, v]) =>
        field === 'prompt_sections'
          ? 'Проверьте инструкции оператора.'
          : Array.isArray(v)
            ? v.join(' ')
            : String(v)
      )
      .join(' ');
    if (message) return { message, sections };
  }
  if (error instanceof Error) return { message: error.message, sections };
  return { message: 'Ошибка при обновлении пользователя', sections };
}

export function useUserEditForm(user: User | null, options: UseUserEditFormOptions = {}) {
  const { onSaved } = options;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // KAN-751: инструкции оператора по частям (user_prompt форма больше не пишет).
  const [promptSections, setPromptSections] = useState<PromptSections>({});
  const [sectionErrors, setSectionErrors] = useState<PromptSectionsErrors | undefined>();
  const [template, setTemplate] = useState<string>('souffleur');
  const [operatorDisplayName, setOperatorDisplayName] = useState('');
  const [useStagePrompts, setUseStagePrompts] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [initialValues, setInitialValues] = useState<InitialValues | null>(null);

  useEffect(() => {
    if (!user) return;
    setFirstName(user.first_name);
    setLastName(user.last_name);
    setPromptSections(user.prompt_sections ?? {});
    setSectionErrors(undefined);
    // TASK-274: шаблон задаётся данными; "souffleur" только если не назначен.
    setTemplate(user.template?.codename ?? 'souffleur');
    setOperatorDisplayName(user.operator_display_name || '');
    setUseStagePrompts(user.use_stage_prompts ?? false);
    setFormError(null);
    // TASK-301: база сравнения без fallback: template=null с бэкенда считается
    // изменением, и "souffleur" уходит в PATCH (лечит NULL в базе).
    setInitialValues({
      firstName: user.first_name,
      lastName: user.last_name,
      promptSections: user.prompt_sections ?? {},
      template: user.template?.codename ?? null,
      operatorDisplayName: user.operator_display_name || '',
      useStagePrompts: user.use_stage_prompts ?? false,
    });
  }, [user]);

  const isDirty = useMemo(() => {
    if (!initialValues) return false;
    return (
      firstName !== initialValues.firstName ||
      lastName !== initialValues.lastName ||
      !promptSectionsEqual(promptSections, initialValues.promptSections) ||
      template !== initialValues.template ||
      operatorDisplayName !== initialValues.operatorDisplayName ||
      useStagePrompts !== initialValues.useStagePrompts
    );
  }, [firstName, lastName, promptSections, template, operatorDisplayName, useStagePrompts, initialValues]);

  // TASK-274: бэкенд отдаёт только шаблоны, подходящие режиму кабинета.
  const { data: roleTemplates = [] } = useQuery({
    queryKey: ['role-templates'],
    queryFn: roleTemplatesApi.list,
  });

  const selectedTemplate = useMemo(
    () => roleTemplates.find(t => t.codename === template),
    [roleTemplates, template]
  );

  // souffleur - единственный внутренний шаблон; остальные общаются с клиентом.
  const isSouffleur = template === 'souffleur';
  const supportsStagePrompts = selectedTemplate?.use_stage ?? user?.template?.use_stage ?? false;
  const isOperatorValid = isSouffleur || operatorDisplayName.trim().length > 0;
  // TASK-349 AC-3: имя обязательно для клиентских шаблонов, пустое подсвечиваем.
  const isDisplayNameMissing = !isSouffleur && operatorDisplayName.trim().length === 0;

  const mutation = useMutation({
    mutationFn: (payload: UpdateUserPayload) => usersApi.update(user!.id, payload),
    onSuccess: () => onSaved?.(),
    onError: (error: unknown) => {
      const { message, sections } = errorMessage(error);
      // KAN-751: ошибки по частям инструкций показываем у самих частей.
      setSectionErrors(sections);
      setFormError(message);
    },
  });

  const buildPayload = useCallback((): UpdateUserPayload => {
    const payload: UpdateUserPayload = {};
    if (firstName !== initialValues?.firstName) payload.first_name = firstName;
    if (lastName !== initialValues?.lastName) payload.last_name = lastName;
    if (!promptSectionsEqual(promptSections, initialValues?.promptSections ?? {})) {
      payload.prompt_sections = promptSections;
    }
    if (template !== initialValues?.template) payload.template = template;
    if (operatorDisplayName.trim() !== (initialValues?.operatorDisplayName ?? '').trim()) {
      payload.operator_display_name = operatorDisplayName.trim();
    }
    if (useStagePrompts !== initialValues?.useStagePrompts) {
      payload.use_stage_prompts = useStagePrompts;
    }
    return payload;
  }, [firstName, lastName, promptSections, template, operatorDisplayName, useStagePrompts, initialValues]);

  /** Отправляет только изменённые поля; без изменений ничего не делает. */
  const submit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      setFormError(null);
      setSectionErrors(undefined);
      if (!user) return;
      const payload = buildPayload();
      if (Object.keys(payload).length > 0) mutation.mutate(payload);
    },
    [user, buildPayload, mutation]
  );

  return {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    promptSections,
    setPromptSections,
    sectionErrors,
    template,
    setTemplate,
    operatorDisplayName,
    setOperatorDisplayName,
    useStagePrompts,
    setUseStagePrompts,
    roleTemplates,
    isSouffleur,
    supportsStagePrompts,
    isOperatorValid,
    isDisplayNameMissing,
    isDirty,
    formError,
    submit,
    isPending: mutation.isPending,
  };
}

export type UserEditForm = ReturnType<typeof useUserEditForm>;
